import { supabase } from '../lib/supabase';

export const orderService = {
  // Get all orders with optional filters
  async getAllOrders(filters = {}) {
    try {
              let query = supabase?.from('orders')?.select(`
            *,
            vehicle:vehicles(*),
            car_house:car_houses(name, code, city),
            assigned_technician:technicians!assigned_technician_id(first_name, last_name, email),
            created_by:user_profiles!created_by_id(full_name),
            service_type:service_types(*)
          `)?.order('created_at', { ascending: false });

      // Apply filters dynamically
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }
      if (filters?.car_house_id) {
        query = query?.eq('car_house_id', filters?.car_house_id);
      }
      if (filters?.assigned_technician_id) {
        query = query?.eq('assigned_technician_id', filters?.assigned_technician_id);
      }
      if (filters?.search) {
        query = query?.or(`order_number.ilike.%${filters?.search}%,customer_name.ilike.%${filters?.search}%,title.ilike.%${filters?.search}%`);
      }

      // Apply date filters
      if (filters?.date_from) {
        query = query?.gte('created_at', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('created_at', filters?.date_to);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { data: null, error };
    }
  },

  // Get order by ID with detailed information
  async getOrderById(id) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*),
          assigned_technician:technicians!assigned_technician_id(*),
          created_by:user_profiles!created_by_id(*),
          order_service_items(
            *,
            service_item:service_items(*)
          ),
          attachments(*),
          audit_logs(
            *,
            user:user_profiles(full_name, email)
          )
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching order:', error);
      return { data: null, error };
    }
  },

  // Create new order
  async createOrder(orderData, userId) {
    try {
      console.log('orderService.createOrder called with:', { orderData, userId });
      
      // Ensure we're not passing any ID field
      const { id, ...cleanOrderData } = orderData;
      if (id) {
        console.warn('Removing ID from order data:', id);
      }
      
      const finalOrderData = {
        ...cleanOrderData,
        created_by_id: userId
      };
      
      console.log('Final order data to insert:', finalOrderData);
      
      const { data, error } = await supabase?.from('orders')?.insert([finalOrderData])?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*)
        `)?.single();

      console.log('Database insert result:', { data, error });

      if (error) throw error;

      // Create audit log
      if (data) {
        await this.createAuditLog({
          user_id: userId,
          order_id: data?.id,
          action: 'created',
          table_name: 'orders',
          record_id: data?.id,
          new_values: orderData
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating order:', error);
      return { data: null, error };
    }
  },

  // Update order
  async updateOrder(id, updates, userId) {
    try {
      console.log('orderService.updateOrder called with:', { id, updates, userId });
      
      // Get current data for audit log
      const { data: currentData } = await supabase?.from('orders')?.select('*')?.eq('id', id)?.single();
      console.log('Current order data:', currentData);

      // Log the specific update being attempted
      console.log('Attempting to update order with:', updates);
      console.log('Status change from:', currentData?.status, 'to:', updates?.status);

      // If status is being changed to 'completed', handle completion logic
      if (updates?.status === 'completed' && currentData?.status !== 'completed') {
        console.log('Order being marked as completed, handling completion logic...');
        
        // Set actual completion time if not already set
        if (!currentData?.actual_completion) {
          updates.actual_completion = new Date().toISOString();
        }
        
        // Always preserve the existing total amount first
        if (currentData?.total_amount && !updates.total_amount) {
          updates.total_amount = currentData.total_amount;
          console.log('Preserving existing total amount:', currentData.total_amount);
        }
        
        // Try to calculate order totals using the simple function
        try {
          const { data: totalData, error: totalError } = await supabase?.rpc('calculate_order_totals_simple', {
            order_uuid: id
          });
          
          if (totalError) {
            console.error('Error calculating order totals:', totalError);
            console.log('Keeping existing total amount due to calculation error');
          } else if (totalData && parseFloat(totalData) > 0) {
            // Only update if we got a valid positive total
            updates.total_amount = parseFloat(totalData);
            console.log('Order totals calculated successfully:', totalData);
          } else {
            console.log('Calculation returned invalid total, keeping existing amount');
          }
        } catch (totalError) {
          console.error('Exception calculating order totals:', totalError);
          console.log('Keeping existing total amount due to exception');
        }
      }

      const { data, error } = await supabase?.from('orders')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select(`
          *,
          vehicle:vehicles(*),
          car_house:car_houses(*),
          assigned_technician:technicians!assigned_technician_id(*)
        `)?.single();

      console.log('Update result:', { data, error });
      
      if (error) {
        console.error('Supabase update error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('🔍 Checking what was actually saved in database:', {
        sentTotalAmount: updates.total_amount,
        receivedTotalAmount: data?.total_amount,
        sentServiceTypeId: updates.service_type_id,
        receivedServiceTypeId: data?.service_type_id,
        sentStatus: updates.status,
        receivedStatus: data?.status
      });

      // Create audit log
      if (data && currentData) {
        await this.createAuditLog({
          user_id: userId,
          order_id: id,
          action: 'updated',
          table_name: 'orders',
          record_id: id,
          old_values: currentData,
          new_values: updates
        });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating order:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      return { data: null, error };
    }
  },

  // Assign technician to order
  async assignTechnician(orderId, technicianId, userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.update({
          assigned_technician_id: technicianId,
          status: 'assigned',
          updated_at: new Date()?.toISOString()
        })?.eq('id', orderId)?.select(`
          *,
          assigned_technician:technicians!assigned_technician_id(*)
        `)?.single();

      if (error) throw error;

      // Create notification for the assigned technician
      if (data && technicianId) {
        await this.createNotification({
          user_id: technicianId,
          order_id: orderId,
          type: 'order_assigned',
          title: 'New Order Assigned',
          message: `You have been assigned order ${data?.order_number}`
        });
      }

      // Create audit log
      await this.createAuditLog({
        user_id: userId,
        order_id: orderId,
        action: 'assigned',
        table_name: 'orders',
        record_id: orderId,
        new_values: { assigned_technician_id: technicianId }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error assigning technician:', error);
      return { data: null, error };
    }
  },

  // Bulk assign technicians
  async bulkAssignTechnician(orderIds, technicianId, userId) {
    try {
      const { data, error } = await supabase?.from('orders')?.update({
          assigned_technician_id: technicianId,
          status: 'assigned',
          updated_at: new Date()?.toISOString()
        })?.in('id', orderIds)?.select('id, order_number');

      if (error) throw error;

      // Create notifications and audit logs for each order
      if (data && technicianId) {
        const notifications = data?.map(order => ({
          user_id: technicianId,
          order_id: order?.id,
          type: 'order_assigned',
          title: 'New Order Assigned',
          message: `You have been assigned order ${order?.order_number}`,
          created_at: new Date()?.toISOString()
        }));

        await supabase?.from('notifications')?.insert(notifications);

        // Create audit logs
        for (const order of data) {
          await this.createAuditLog({
            user_id: userId,
            order_id: order?.id,
            action: 'assigned',
            table_name: 'orders',
            record_id: order?.id,
            new_values: { assigned_technician_id: technicianId }
          });
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error bulk assigning technician:', error);
      return { data: null, error };
    }
  },

  // Get orders for technician mobile view
  async getTechnicianOrders(technicianId, filters = {}) {
    try {
      let query = supabase?.from('orders')?.select(`
          *,
          vehicle:vehicles(make, model, year, vin),
          car_house:car_houses(name),
          order_service_items(
            *,
            service_item:service_items(name, estimated_duration_hours)
          )
        `)?.eq('assigned_technician_id', technicianId)?.order('appointment_date', { ascending: true });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching technician orders:', error);
      return { data: null, error };
    }
  },

  // Calculate order totals using Supabase function
  async calculateOrderTotals(orderId) {
    try {
      const { data, error } = await supabase?.rpc('calculate_order_totals', {
          order_uuid: orderId
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error calculating order totals:', error);
      return { data: null, error };
    }
  },

  // Create audit log entry
  async createAuditLog(logData) {
    try {
      const { data, error } = await supabase?.rpc('create_audit_log', {
          p_user_id: logData?.user_id,
          p_order_id: logData?.order_id,
          p_action: logData?.action,
          p_table_name: logData?.table_name,
          p_record_id: logData?.record_id,
          p_old_values: logData?.old_values || null,
          p_new_values: logData?.new_values || null
        });

      return { data, error };
    } catch (error) {
      console.error('Error creating audit log:', error);
      return { data: null, error };
    }
  },

  // Create notification
  async createNotification(notificationData) {
    try {
      const { data, error } = await supabase?.from('notifications')?.insert([{
          ...notificationData,
          created_at: new Date()?.toISOString()
        }])?.single();

      return { data, error };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }
  },

  // Get order statistics
  async getOrderStatistics(filters = {}) {
    try {
      let query = supabase?.from('orders')?.select('status, priority, total_amount, created_at, car_house_id');

      // Apply date filters
      if (filters?.date_from) {
        query = query?.gte('created_at', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('created_at', filters?.date_to);
      }
      if (filters?.car_house_id) {
        query = query?.eq('car_house_id', filters?.car_house_id);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Calculate statistics
      const stats = {
        total_orders: data?.length || 0,
        completed_orders: data?.filter(order => order?.status === 'completed')?.length || 0,
        in_progress_orders: data?.filter(order => order?.status === 'in_progress')?.length || 0,
        pending_orders: data?.filter(order => order?.status === 'pending')?.length || 0,
        total_revenue: data?.reduce((sum, order) => sum + (parseFloat(order?.total_amount) || 0), 0) || 0,
        avg_order_value: 0
      };

      stats.avg_order_value = stats?.total_orders > 0 ? stats?.total_revenue / stats?.total_orders : 0;

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching order statistics:', error);
      return { data: null, error };
    }
  },

  // Check technician availability
  async checkTechnicianAvailability(technicianId, appointmentDate) {
    try {
      const { data, error } = await supabase?.rpc('is_technician_available', {
          technician_uuid: technicianId,
          check_datetime: appointmentDate
        });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error checking technician availability:', error);
      return { data: false, error };
    }
  },

  // Delete order
  async deleteOrder(orderId, userId) {
    try {
      // Get the order data before deletion to find the VIN
      const { data: orderData, error: fetchError } = await supabase
        ?.from('orders')
        ?.select('vehicle_id')
        ?.eq('id', orderId)
        ?.single();

      if (fetchError) {
        console.error('Error fetching order data before deletion:', fetchError);
      }

      // Delete the order
      const { error } = await supabase?.from('orders')?.delete()?.eq('id', orderId);

      if (error) throw error;

      // Create audit log
      await this.createAuditLog({
        user_id: userId,
        order_id: orderId,
        action: 'deleted',
        table_name: 'orders',
        record_id: orderId,
        old_values: { order_id: orderId }
      });

      // Update scan history to mark as not having order
      if (orderData?.vehicle_id) {
        try {
          // Get the VIN from the vehicle
          const { data: vehicleData, error: vehicleError } = await supabase
            ?.from('vehicles')
            ?.select('vin')
            ?.eq('id', orderData.vehicle_id)
            ?.single();

          if (!vehicleError && vehicleData?.vin) {
            // Import scanHistoryService dynamically to avoid circular dependencies
            const { default: scanHistoryService } = await import('./scanHistoryService.js');
            await scanHistoryService.updateScanOrderStatus(vehicleData.vin, userId, false);
          }
        } catch (scanError) {
          console.error('Error updating scan history after order deletion:', scanError);
        }
      }

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting order:', error);
      return { data: null, error };
    }
  }
};

export default orderService;